import React from 'react';
import ReactDOM from 'react-dom';

export interface PortalProps {
  /**
   * The elements to render inside the portal container
   */
  children: React.ReactNode;
  /**
   * ID attribute to attach to the DOM element representing the portal
   */
  id?: string;
  /**
   * owner document inside which portal element has to be placed
   */
  ownerDocument?: Document | null;
}

/**
 * Render content at the end of the document body
 *
 * @version PACKAGE_VERSION
 * @tutorial http://in/ids-storybook
 */
const Portal = ({
  children,
  id,
  ownerDocument = typeof document === 'undefined' ? null : document
}: PortalProps) => {
  const portalContainer = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    return () => {
      if (
        portalContainer.current &&
        ownerDocument &&
        portalContainer.current.parentElement === ownerDocument.body
      ) {
        ownerDocument &&
          ownerDocument.body.removeChild(portalContainer.current);
      }

      portalContainer.current = null;
    };
  }, [ownerDocument]);

  if (!portalContainer.current && ownerDocument) {
    portalContainer.current = ownerDocument.createElement('div');
    if (id) {
      portalContainer.current.setAttribute('id', id);
    }

    ownerDocument.body.appendChild(portalContainer.current);
  }

  return (
    portalContainer.current &&
    ReactDOM.createPortal(children, portalContainer.current)
  );
};

export default Portal;
